import { GiftOutlined, TrophyOutlined } from '@ant-design/icons';
import { Steps, Button, message, Spin, Modal } from 'antd';
import { motion } from 'motion/react';
import ConfettiExplosion from 'react-confetti-explosion';
import './styles.scss';
import { snappy } from 'src/_shared';
import { useState, useEffect } from 'react';
import GiftImage from 'src/_shared/assets/snappy_present.png';

const confettiConfig = {
	force: 0.9,
	duration: 3000,
	particleCount: 300,
	width: '2000',
	height: 1600,
	colors: ['#f650a9', '#00c3ff', '#fffc00', '#00ff87', '#ff0055', '#7700ff']
};

const RewardsProgressIndicator = ({ tiers, currentUser }) => {
	const [giftsByCampaignTierId, setGiftsByCampaignTierId] = useState({});
	const [isLoading, setIsLoading] = useState(true);
	const [processingTiers, setProcessingTiers] = useState(new Set());
	const [showGiftModal, setShowGiftModal] = useState(false);
	const [isExploding, setIsExploding] = useState(false);
	const [claimedGiftData, setClaimedGiftData] = useState(null);

	useEffect(() => {
		getCampaigns();
	}, []);

	const handleClaimGift = async (tier) => {
		setProcessingTiers(prev => new Set([...prev, tier.id]));
		
		try {
			const response = await snappy({
				type: 'createGifts',
				userId: currentUser.id,
				sendingMethod: 'link',
				recipients: [currentUser.id],
				campaignTierId: tier.id,
			});

			if (response && response.length > 0) {
				const result = response[0];
				if (result.giftId && result.giftDetails && result.giftDetails.link) {
					await getCampaigns();
					
					setClaimedGiftData({
						id: result.giftId,
						snappyUrl: result.giftDetails.link,
						title: 'Your Reward is Ready!',
						tierName: tier.tierName
					});
					
					setShowGiftModal(true);
					setIsExploding(true);
				} else {
					message.error(result.errorMessage || 'Error claiming gift.');
					console.error('Error claiming gift:', result.errorMessage);
				}
			} else {
				message.error('Unexpected response from the server.');
				console.error('Unexpected response:', response);
			}
		} catch (error) {
			console.error('Error claiming gift', error);
			message.error('An error occurred while claiming the gift.');
		} finally {
			setProcessingTiers(prev => {
				const newSet = new Set(prev);
				newSet.delete(tier.id);
				return newSet;
			});
		}
	};

	const handleDirectLink = (gift) => {
		if (gift?.giftDetails?.link) {
			window.open(gift.giftDetails.link, '_blank');
		}
	};

	const handleGiftModalClose = () => {
		setShowGiftModal(false);
		setIsExploding(false);
		setClaimedGiftData(null);
	};

	const handleGiftRedeem = () => {
		if (claimedGiftData?.snappyUrl) {
			window.open(claimedGiftData.snappyUrl, '_blank');
		}
		handleGiftModalClose();
	};

	const getCampaigns = async () => {
		setIsLoading(true);
		try {
			const response = await snappy({
				type: 'getCampaigns',
				userId: currentUser.id,
			});
			processCampaigns(response || []);
		} catch (error) {
			console.error('Error getCampaigns:', error);
		} finally {
			setIsLoading(false);
		}
	};

	const processCampaigns = (campaignsData) => {
		const giftsMap = {};
		campaignsData.forEach((campaign) => {
			campaign.tiers.forEach((tier) => {
				if (tier.gift) {
					giftsMap[tier.id] = tier.gift;
				}
			});
		});
		setGiftsByCampaignTierId(giftsMap);
	};

	if (isLoading) {
		return (
			<div style={{ textAlign: 'center', padding: '24px' }}>
				<Spin size="large" />
			</div>
		);
	}

	const items = tiers.map((tier) => {
		const isClaimable = tier.userBadgePoints >= tier.pointsRequired;
		const gift = giftsByCampaignTierId[tier.id];
		const isProcessing = processingTiers.has(tier.id);

		let button;
		let icon;
		let status;
		let isClaimed = false;

		if (gift) {
			const giftStatus = gift?.giftDetails?.status;
			const hasLink = gift?.giftDetails?.link;
			
			if (giftStatus === 'claimed') {
				button = <strong>Claimed</strong>;
				icon = <TrophyOutlined />;
				status = 'finish';
				isClaimed = true;
			} else if (hasLink) {
				button = (
					<Button
						style={{ fontSize: '15px' }}
						size="large"
						type="link"
						onClick={() => handleDirectLink(gift)}
					>
						<strong style={{maxWidth: 120, textWrap: 'auto'}}>Finish Claiming Reward</strong>
					</Button>
				);
				icon = <TrophyOutlined />;
				status = 'finish';
				isClaimed = true;
			} else {
				button = (
					<Button
						style={{ fontSize: '15px' }}
						size="large"
						type="link"
						onClick={() => handleClaimGift(tier)}
						loading={isProcessing}
						disabled={isProcessing}
					>
						<strong>{isProcessing ? 'Processing...' : 'Claim Reward'}</strong>
					</Button>
				);
				icon = <TrophyOutlined />;
				status = 'finish';
				isClaimed = true;
			}
		} else if (isClaimable) {
			button = (
				<Button
					style={{ fontSize: '15px' }}
					size="large"
					type="link"
					onClick={() => handleClaimGift(tier)}
					loading={isProcessing}
					disabled={isProcessing}
				>
					<strong>{isProcessing ? 'Processing...' : 'Claim Reward'}</strong>
				</Button>
			);
			icon = <GiftOutlined />;
			status = 'finish';
		} else {
			button = <strong>{tier.pointsRequired} points</strong>;
			icon = <GiftOutlined />;
			status = 'wait';
		}

		return {
			title: <p className="rewards-progress-reward-level">{tier.tierName}</p>,
			description: (
				<>
					<p className="rewards-progress-reward-description">
						{tier.tierDescription}
					</p>
					<p
						className="rewards-progress-reward-points"
						style={{ color: isClaimable ? 'green' : 'gray' }}
					>
						{button}
					</p>
				</>
			),
			icon: (
				<div
					className={`icon-wrapper ${isClaimable ? 'claimed' : ''}`}
					style={{
						backgroundColor: isClaimable ? '#f650a9' : 'lightgrey',
					}}
				>
					{icon}
				</div>
			),
			status,
		};
	});

	const itemIsClaimable = items.map((item, index) => {
		return {
			index,
			isClaimable: tiers[index].userBadgePoints >= tiers[index].pointsRequired,
		};
	});

	for (let i = 1; i < items.length; i++) {
		if (itemIsClaimable[i - 1].isClaimable && itemIsClaimable[i].isClaimable) {
			items[i - 1].className = 'tail-pink';
		}
	}

	const currentStepIndex = tiers.findIndex(
		(tier) => tier.userBadgePoints < tier.pointsRequired
	);

	return (
		<div>
			<Steps
				current={currentStepIndex === -1 ? tiers.length : currentStepIndex}
				labelPlacement="vertical"
				items={items}

			/>
			
			{/* Confetti Gift Modal */}
			<Modal
				title={null}
				open={showGiftModal}
				onCancel={handleGiftModalClose}
				footer={[
					<Button key="redeem" type="primary" onClick={handleGiftRedeem}>
						Go to Snappy
					</Button>,
					<Button key="close" onClick={handleGiftModalClose}>
						Close
					</Button>
				]}
				centered
				width={500}
			>
				{isExploding && (
					<div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 9999 }}>
						<ConfettiExplosion {...confettiConfig} />
					</div>
				)}
				
				<div style={{ textAlign: 'center', padding: '20px 0' }}>
					<motion.div
						initial={{ scale: 0.8, opacity: 0 }}
						animate={{ scale: 1, opacity: 1 }}
						transition={{
							type: "spring",
							stiffness: 200,
							damping: 20
						}}
						style={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							gap: '20px'
						}}
					>
						<img
							src={GiftImage}
							alt="Gift"
							style={{
								width: '120px',
								height: '120px',
								objectFit: 'contain'
							}}
						/>
						
						<div>
							<h2 style={{ color: '#f650a9', marginBottom: '8px', fontSize: '24px' }}>
								{claimedGiftData?.title || 'Your Reward is Ready!'}
							</h2>
							{claimedGiftData?.tierName && (
								<p style={{ fontSize: '16px', color: '#666', margin: 0 }}>
									{claimedGiftData.tierName}
								</p>
							)}
						</div>
						
						<p style={{ fontSize: '16px', color: '#666', textAlign: 'center', maxWidth: '300px' }}>
							Click "Go to Snappy" to redeem your reward!
						</p>
					</motion.div>
				</div>
			</Modal>
		</div>
	);
};

export default RewardsProgressIndicator;
